import { useCallback, useMemo, useState } from "react";
import type { SchemaRegion } from "../../../api/restaurantSchema";
import type { OpenTable, Reservation } from "../../../api/reservations";
import { useReservationsDay } from "../../../hooks/useReservations";
import {
  TIMELINE_DEFAULT_START_MINUTE,
  clampToTimeline,
  minuteOfDay,
  momentAtMinute,
  snapToStep,
  toDateKey,
} from "../logic/dayTime";
import { reservationStartMs } from "../logic/tableStatus";
import { dayTimelineRange } from "../logic/timelineDay";

/** Дясната колона: списъкът с резервации или формата „Нова резервация". */
export type RightPanel = "list" | "new";

/** С какво се отваря „Нова резервация" (от поповъра „Резервирай тази маса"). */
export interface NewReservationSeed {
  tableId: number | null;
  // Моментът от времевата линия, когато е натиснат бутонът.
  startMs: number | null;
}

const NO_RESERVATIONS: Reservation[] = [];
const NO_OPEN_TABLES: OpenTable[] = [];

/**
 * Минутата за часа на резервацията (надолу до 5 мин). Линията започва от
 * най-ранната резервация на деня (`dayTimelineRange`), затова часът ѝ е
 * винаги в обхвата.
 */
const timelineMinuteOf = (reservation: Reservation) =>
  snapToStep(minuteOfDay(reservationStartMs(reservation)));

/**
 * Къде да отиде линията при клик на карта: настанената/приключилата — в
 * минутата на настаняването (иначе към часа на резервацията масата може още
 * да не е заета — настанени със закъснение — и поповърът показва „Свободна");
 * останалите — към часа на резервацията.
 */
const focusMinuteOf = (reservation: Reservation) =>
  (reservation.status === "completed" || reservation.status === "seated") && reservation.seatedAt
    ? minuteOfDay(Date.parse(reservation.seatedAt))
    : timelineMinuteOf(reservation);

/**
 * Цялото състояние на екрана „Резервации" на едно място: денят, моментът от
 * времевата линия („сега" или закован час), регионът, отвореният поповър,
 * подчертаната резервация и дясната колона. Функциите `focus*` са входът за
 * списъка: клик на карта → регионът на масата, часът и поповърът.
 * Зарежда и резервациите на избрания ден (`dayQuery`, опреснява се на 15 s).
 */
export const useReservationsView = ({
  regions,
  nowMs,
  enabled,
}: {
  regions: SchemaRegion[];
  nowMs: number;
  // false — без заявки (без интернет).
  enabled: boolean;
}) => {
  const todayKey = toDateKey(new Date(nowMs));
  // null = днес (и след полунощ остава „днес").
  const [pickedDateKey, setPickedDateKey] = useState<string | null>(null);
  // null = „сега" (днес) / началото на деня (друг ден).
  const [pinnedMinute, setPinnedMinute] = useState<number | null>(null);
  const [activeRegionId, setActiveRegionId] = useState<number | null>(null);
  const [openTableId, setOpenTableId] = useState<number | null>(null);
  const [selectedReservationId, setSelectedReservationId] = useState<number | null>(null);
  const [rightPanel, setRightPanel] = useState<RightPanel>("list");

  const dateKey = pickedDateKey ?? todayKey;
  const isToday = dateKey === todayKey;
  const dayQuery = useReservationsDay(dateKey, { enabled });
  const reservations = dayQuery.data?.reservations ?? NO_RESERVATIONS;
  const isAtNow = isToday && pinnedMinute === null;

  // Друг ден започва от първата резервация (или от 11:00).
  const dayStartMinute = useMemo(() => {
    const starts = reservations
      .filter((reservation) => reservation.status !== "cancelled")
      .map(timelineMinuteOf);
    return starts.length > 0 ? Math.min(...starts) : TIMELINE_DEFAULT_START_MINUTE;
  }, [reservations]);

  // Обхватът на линията: от 11:00 или по-рано (най-ранното на деня) до 24:00.
  // „Сега" преди 11:00 (днес) също я разширява назад.
  const openTables = dayQuery.data?.openTables ?? NO_OPEN_TABLES;
  const timelineRange = useMemo(
    () => dayTimelineRange({ dateKey, reservations, openTables, nowMs }),
    [dateKey, reservations, openTables, nowMs],
  );

  // Истинската минута на момента; плъзгачът я показва клампната (извън
  // обхвата е само по изключение — напр. закован час от друг ден).
  const momentMinute = isAtNow ? minuteOfDay(nowMs) : (pinnedMinute ?? dayStartMinute);
  const sliderMinute = clampToTimeline(momentMinute, timelineRange);
  const momentMs = isAtNow ? nowMs : momentAtMinute(dateKey, momentMinute);

  const region = regions.find((candidate) => candidate.id === activeRegionId) ?? regions[0];

  const regionOfTable = useCallback(
    (tableId: number) =>
      regions.find((candidate) =>
        candidate.elements.some((element) => element.id === tableId && element.kind === "table"),
      ),
    [regions],
  );

  const goToDay = useCallback(
    (nextDateKey: string) => {
      setPickedDateKey(nextDateKey === todayKey ? null : nextDateKey);
      setPinnedMinute(null);
      setOpenTableId(null);
      setSelectedReservationId(null);
    },
    [todayKey],
  );

  /** „● Сега": днешният ден и живият час. */
  const goToNow = useCallback(() => {
    setPickedDateKey(null);
    setPinnedMinute(null);
  }, []);

  /** Плъзгачът: текущата минута днес = „сега" (живият час), не закован час. */
  const setMomentMinute = useCallback(
    (minute: number) => setPinnedMinute(isToday && minute === minuteOfDay(nowMs) ? null : minute),
    [isToday, nowMs],
  );

  const selectRegion = useCallback((regionId: number) => {
    setActiveRegionId(regionId);
    setOpenTableId(null);
  }, []);

  const closePopover = useCallback(() => setOpenTableId(null), []);

  /**
   * Показва масата: превключва на региона ѝ, по желание мести линията към
   * `minute` и отваря поповъра ѝ.
   */
  const focusTable = useCallback(
    (tableId: number, options: { minute?: number | null; openPopover?: boolean } = {}) => {
      const tableRegion = regionOfTable(tableId);
      if (!tableRegion) return;
      setActiveRegionId(tableRegion.id);
      if (options.minute !== undefined) setPinnedMinute(options.minute);
      setOpenTableId(options.openPopover === false ? null : tableId);
    },
    [regionOfTable],
  );

  /**
   * Клик на карта в списъка: подчертава резервацията (карта + ореол на
   * масите), превключва на региона на първата ѝ маса и отваря поповъра.
   * Предстоящите и минали местят линията към часа си; настанената и
   * ЗАКЪСНЯЛАТА предстояща днес връщат към „сега" (иначе след „Настани" от
   * поповъра линията стои на минал час и масата изглежда „Свободна").
   */
  const focusReservation = useCallback(
    (reservation: Reservation) => {
      setSelectedReservationId(reservation.id);
      const isLateToday = reservation.status === "upcoming" && reservationStartMs(reservation) <= nowMs;
      const followsNow = isToday && (reservation.status === "seated" || isLateToday);
      const minute = followsNow ? null : focusMinuteOf(reservation);
      const tableId = reservation.tableIds.find((id) => regionOfTable(id));
      if (tableId === undefined) {
        setPinnedMinute(minute);
        setOpenTableId(null);
        return;
      }
      focusTable(tableId, { minute });
    },
    [focusTable, isToday, nowMs, regionOfTable],
  );

  /**
   * Резервацията, върната в „Предстоящи" („Върни" / „Отмени настаняването")
   * или отворена за редакция: подчертава я и превключва на региона на масата
   * ѝ, ако платното е на друг регион — линията на часа и отвореният поповър
   * (на нейната маса) остават.
   */
  const highlightReservation = useCallback(
    (reservation: Reservation) => {
      setSelectedReservationId(reservation.id);
      const tableRegion = reservation.tableIds.map(regionOfTable).find(Boolean);
      if (tableRegion && tableRegion.id !== region?.id) {
        setActiveRegionId(tableRegion.id);
        setOpenTableId(null);
      }
    },
    [regionOfTable, region?.id],
  );

  /**
   * Току-що създадената резервация: денят ѝ, линията на часа ѝ, регионът на
   * първата маса и подчертаване (карта + ореол), без поповър.
   */
  const revealReservation = useCallback(
    (reservation: Reservation) => {
      const reservationDateKey = toDateKey(new Date(reservationStartMs(reservation)));
      setPickedDateKey(reservationDateKey === todayKey ? null : reservationDateKey);
      setPinnedMinute(timelineMinuteOf(reservation));
      setSelectedReservationId(reservation.id);
      setOpenTableId(null);
      const tableRegion = reservation.tableIds.map(regionOfTable).find(Boolean);
      if (tableRegion) setActiveRegionId(tableRegion.id);
    },
    [regionOfTable, todayKey],
  );

  /**
   * „+ Нова резервация" / „Резервирай тази маса" — дясната колона става
   * формата (състоянието ѝ, вкл. масата и часа от поповъра, е в
   * `useNewReservationForm`).
   */
  const openNewReservation = useCallback(() => {
    setOpenTableId(null);
    setRightPanel("new");
  }, []);

  const closeNewReservation = useCallback(() => setRightPanel("list"), []);

  return {
    dayQuery,
    reservations,
    // Резервациите на избрания ден още не са дошли (placeholder-ът носи само
    // отворените сметки) — броячите/списъкът не бива да казват „0".
    isDayPending: dayQuery.isPlaceholderData,
    todayKey,
    dateKey,
    isToday,
    isAtNow,
    // Потребителят е закрепил час на линията (не е „сега" / началото на деня).
    isPinned: pinnedMinute !== null,
    sliderMinute,
    // Обхватът и надписите на линията за деня.
    timelineRange,
    // Моментът е извън обхвата на линията (палецът стои на ръба).
    isOutsideTimeline: momentMinute !== sliderMinute,
    momentMs,
    region,
    openTableId,
    selectedReservationId,
    rightPanel,
    goToDay,
    goToNow,
    setMomentMinute,
    selectRegion,
    openPopover: setOpenTableId as (tableId: number) => void,
    closePopover,
    selectReservation: setSelectedReservationId,
    focusTable,
    focusReservation,
    highlightReservation,
    revealReservation,
    openNewReservation,
    closeNewReservation,
  };
};

export type ReservationsView = ReturnType<typeof useReservationsView>;
